import { useAccount } from "@qw/auth";
import { type InboxContactFragment, useInboxContactsQuery } from "@qw/graphql";
import { createContext, useCallback, useContext, useMemo, useRef } from "react";

const CONTACTS_POLL_INTERVAL = 1000 * 60 * 10; // 10 minutes
const CONTACTS_ERROR_BACKOFF_INTERVAL = 1000 * 60 * 30; // 30 minutes

export interface ContactsContextValue {
  contacts: InboxContactFragment[];
  isLoading: boolean;
}

export const ContactsContext = createContext<ContactsContextValue | null>(null);

export const ContactsProvider = ({
  children,
  pollInterval = CONTACTS_POLL_INTERVAL,
}: {
  children: React.ReactNode;
  pollInterval?: number;
}) => {
  const { user } = useAccount();
  const pollIntervalRef = useRef(pollInterval);

  const onError = useCallback(() => {
    pollIntervalRef.current = CONTACTS_ERROR_BACKOFF_INTERVAL;
  }, []);

  const onCompleted = useCallback(() => {
    pollIntervalRef.current = pollInterval;
  }, [pollInterval]);

  const { data, loading } = useInboxContactsQuery({
    skip: !user,
    pollInterval: pollIntervalRef.current,
    fetchPolicy: "cache-and-network",
    onError,
    onCompleted,
  });

  const value = useMemo(
    () => ({ contacts: data?.inboxContacts ?? [], isLoading: loading }),
    [data?.inboxContacts, loading]
  );

  return <ContactsContext.Provider value={value}>{children}</ContactsContext.Provider>;
};

export const useContacts = () => {
  const context = useContext(ContactsContext);

  if (!context) {
    throw new Error("useContacts must be used within a ContactsProvider");
  }

  return context;
};
